import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Email, Visibility, Gavel } from '@mui/icons-material';
import { clearImpersonationStash } from '../services/impersonationSession';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const TOU_VERSION = '2026-04-07-v1';

const DemoLogin: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [touOpen, setTouOpen] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const touContentRef = useRef<HTMLDivElement>(null);

  // OTP state
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [otpCode, setOtpCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleTouScroll = useCallback(() => {
    const el = touContentRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (atBottom) setScrolledToBottom(true);
  }, []);

  const handleAcceptTou = () => {
    setAcceptedTerms(true);
    setTouOpen(false);
  };

  const startResendCooldown = useCallback(() => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleLoginSuccess = (data: any) => {
    // A new sign-in owns this browser: a stash left by an unfinished view-as must never outlive it.
    clearImpersonationStash();
    sessionStorage.setItem('prism_token', data.data.token);
    sessionStorage.setItem('prism_user', JSON.stringify(data.data.user));
    navigate('/dashboard');
  };

  const handleDemoAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms) {
      setError('Please accept the Terms of Use to continue.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, accepted_terms: true, tou_version: TOU_VERSION }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Demo access failed. Please try again.');
        return;
      }

      // OTP flow: server returns step: 'otp_sent'
      if (data.step === 'otp_sent') {
        setStep('otp');
        startResendCooldown();
        return;
      }

      // Direct flow (no OTP configured): server returns token
      handleLoginSuccess(data);
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/demo/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otpCode }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Verification failed. Please try again.');
        return;
      }

      handleLoginSuccess(data);
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, accepted_terms: true, tou_version: TOU_VERSION }),
      });
      const data = await response.json();
      if (data.success) {
        startResendCooldown();
        setError('');
      } else {
        setError(data.error || 'Failed to resend code.');
      }
    } catch {
      setError('Unable to resend code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0a192f 0%, #112240 50%, #0a192f 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(0,217,255,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: '250px',
          height: '250px',
          background: 'radial-gradient(circle, rgba(100,255,218,0.06) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)',
        },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 5,
          width: '100%',
          maxWidth: 480,
          borderRadius: 3,
          position: 'relative',
          zIndex: 1,
          background: 'rgba(23, 42, 69, 0.8) !important',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 217, 255, 0.15) !important',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box component="h1" sx={{ m: 0, mb: 2, lineHeight: 1 }}>
            <img
              src="/risk-aperture-logo.svg"
              alt="Risk Aperture"
              style={{
                height: 80,
                width: 'auto',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </Box>
          <Typography variant="subtitle1" sx={{ color: '#a8b2cc' }}>
            Self-Guided Demo
          </Typography>
          <Typography variant="body2" sx={{ color: '#7a8aaa', mt: 1 }}>
            Explore PRISM Risk Aperture with a read-only preview of a sample security assessment
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {step === 'email' ? (
          <>
            <form onSubmit={handleDemoAccess}>
              <TextField
                fullWidth
                label="Work Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="We'll follow up to see if PRISM is a fit for your team"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => {
                      if (e.target.checked && !scrolledToBottom) {
                        setTouOpen(true);
                      } else {
                        setAcceptedTerms(e.target.checked);
                      }
                    }}
                    sx={{ color: '#7a8aaa', '&.Mui-checked': { color: '#00d9ff' } }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#a8b2cc' }}>
                    I have read and agree to the{' '}
                    <Link
                      component="button"
                      type="button"
                      sx={{ color: '#00d9ff', verticalAlign: 'baseline' }}
                      onClick={(e) => {
                        e.preventDefault();
                        setTouOpen(true);
                      }}
                    >
                      Demo Terms of Use
                    </Link>
                  </Typography>
                }
                sx={{ mt: 2, alignItems: 'flex-start' }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !email || !acceptedTerms}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Visibility />}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? 'Sending Code...' : 'Start Demo Tour'}
              </Button>
            </form>

            <Typography variant="caption" sx={{ color: '#7a8aaa', display: 'block', textAlign: 'center', mt: 2 }}>
              Already have an account?{' '}
              <Link href="/login" sx={{ color: '#00d9ff' }}>
                Sign in
              </Link>
            </Typography>
          </>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <Typography variant="body2" sx={{ color: '#a8b2cc', mb: 2, textAlign: 'center' }}>
              We sent a 6-digit verification code to <strong style={{ color: '#64ffda' }}>{email}</strong>
            </Typography>

            <TextField
              fullWidth
              label="Verification Code"
              value={otpCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtpCode(val);
              }}
              margin="normal"
              required
              autoFocus
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: 24, letterSpacing: 8, fontFamily: 'monospace' },
              }}
              helperText="Enter the 6-digit code from your email"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || otpCode.length !== 6}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Visibility />}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? 'Verifying...' : 'Verify & Enter Demo'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Link
                component="button"
                type="button"
                sx={{ color: resendCooldown > 0 ? '#7a8aaa' : '#00d9ff', fontSize: 13 }}
                onClick={handleResendOTP}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
              </Link>
              <Link
                component="button"
                type="button"
                sx={{ color: '#7a8aaa', fontSize: 13 }}
                onClick={() => { setStep('email'); setOtpCode(''); setError(''); }}
              >
                Use different email
              </Link>
            </Box>
          </form>
        )}
      </Paper>

      {/* Terms of Use Dialog */}
      <Dialog
        open={touOpen}
        onClose={() => setTouOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#112240',
            color: '#ccd6f6',
            border: '1px solid rgba(0,217,255,0.2)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Gavel sx={{ color: '#00d9ff' }} />
          Demo Terms of Use
        </DialogTitle>
        <DialogContent>
          <Box
            ref={touContentRef}
            onScroll={handleTouScroll}
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              pr: 1,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,217,255,0.3)', borderRadius: 3 },
            }}
          >
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: '#64ffda' }}>
              CONFIDENTIALITY AND NON-DISCLOSURE AGREEMENT
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
              By accessing this demo environment, you acknowledge and agree that:
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>1. Confidentiality.</strong> All information, software, workflows, scoring
              methodologies, visual outputs, and user interface designs you observe during this
              session are Confidential Information of Risk Aperture, LLC and its clients.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>2. No Recording or Capture.</strong> You will not record, screenshot, screen
              capture, or otherwise capture any portion of this session by any means — including
              but not limited to manual tools (e.g., phone camera, print screen), software (e.g.,
              OBS, screen recording utilities), or browser extensions — without prior written
              authorization from Risk Aperture.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>3. AI Extraction Prohibition.</strong> You will not use any AI tool, vision
              model, screenshot-to-code pipeline, UI reconstruction tool (including but not
              limited to v0, Bolt.new, UISnapper, or similar), large language model, or automated
              pipeline to capture, analyze, reconstruct, replicate, or derive any element of this
              software — including its user interface, workflows, scoring algorithms, data models,
              API structures, or business logic.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>4. No Derivative Works.</strong> You will not create, commission, or assist
              in the creation of any software, product, or service that is substantially similar
              to or derived from the information observed in this demo.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>5. Session Monitoring.</strong> This session is logged, timestamped, and
              watermarked for security purposes. Your email address, IP address, session activity,
              and access patterns are recorded and retained.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>6. Notification Obligation.</strong> If you discover any unauthorized
              disclosure or use of information from this demo, you must notify Risk Aperture
              within 48 hours.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>7. Remedies.</strong> You acknowledge that a breach of these terms would
              cause irreparable harm to Risk Aperture, and Risk Aperture shall be entitled to
              seek injunctive relief without the requirement of posting bond, in addition to
              any other remedies available at law or in equity.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, lineHeight: 1.7 }}>
              <strong>8. Duration.</strong> These obligations survive for 5 years from the date
              of access or for as long as the information retains trade secret status, whichever
              is longer.
            </Typography>
            <Typography variant="body2" sx={{ mt: 3, color: '#7a8aaa', fontStyle: 'italic' }}>
              All data shown in this demo is synthetic and does not represent any real organization.
              Version {TOU_VERSION}.
            </Typography>
          </Box>
          {!scrolledToBottom && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 1, textAlign: 'center', color: '#64ffda' }}
            >
              Please scroll to the bottom to accept
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTouOpen(false)} sx={{ color: '#7a8aaa' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!scrolledToBottom}
            onClick={handleAcceptTou}
          >
            I Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DemoLogin;
